import React, { FC, ReactNode } from "react";
import { useUser } from "../../../../../hooks/useUser";
import { isInternal } from "../../../../../utils";
import { IconAlertGlyph, IconClockGlyph } from "../../../videos/selectedVideoCardV2/panels";
import { PollIndicator, RetryButton, ScoreState } from "../../../videos/selectedVideoCardV2/performance/ScoreState";
import type { ISingleProduct } from "../../../productsV2/productListView/constants";
import { SECTION_IDS } from "../components/SectionRail";
import { SectionCard, StateBlockSkeleton } from "../components/SectionCard";
import { focusFirstButton, useRetryFocus } from "../components/focusReturn";
import { TouchTarget } from "../components/TouchTarget";
import { glyph } from "../components/ui";
import { CvScoreCards } from "./CvScoreCards";
import {
  analysedLine,
  cvBrandIdForViewer,
  cvReportMeta,
  cvSettingsHint,
  cvSettingsMatchesPage,
  cvView,
} from "./cvModel";
import { useProductConsumerVoice } from "./useProductConsumerVoice";

/**
 * Section 1 — Consumer Voice, v1 (Karl, 2026-09-28): the six score cards only.
 *
 * Usage (the page):
 *   <ConsumerVoiceSection product={product} scopeBrandId={scope.brandId} reloadKey={sectionsNonce}
 *     pollPaused={settingsOpen} className={offWhileVideo} />
 *
 * Read-only, always in the SectionCard (the video details page's panel
 * chrome). With a ready run: "Analysed for {brand} · {date}" under the title,
 * then the six cards (CvScoreCards) for the NEWEST ready run. A newer pending
 * or failed run changes nothing here; the silent poll swaps the cards when a
 * newer run lands ready. With no ready run: one ScoreState block, as the video
 * page's Page score panel draws its states — loading (a block-sized
 * skeleton), never (empty), a first run in progress (working; polled every 8
 * seconds, silently, so the cards appear when it finishes), a run pending
 * past the 60-minute window (empty, clock: it may still be finishing; not
 * polled), only failed runs (error), Not available (empty, lock) or Couldn't
 * load (error + Retry, announced). Runs, hints, history, the PDF and the full
 * report stay in the settings modal; the full v2 section is parked at
 * product-details-cv-v2-parked.
 *
 * The never / failed blocks point at Settings only when the modal reads the
 * brand the page reads and its Run button is enabled (cvSettingsHint);
 * otherwise a run started there never shows here, or can't be started, so
 * they don't say where to run one.
 *
 * The state blocks are the video page's ScoreState, unchanged: the lock and
 * message glyphs go in aria-hidden (ui.tsx's glyph rule; the panel kit's
 * icons hide themselves), the load error is wrapped in role="alert" (as
 * AlertStrip announces the page's other failures), and Retry is lifted to
 * 44px below md by TouchTarget.
 *
 * If the report call fails, the cards still draw from the list row, with the
 * report's no-meta captions ("Details unavailable." where the report would
 * ask for a rerun) and no banner.
 *
 * Memoised: every prop is page state or a primitive (index.tsx), so none
 * changes when the page re-renders for a router navigation (a Videos filter,
 * sort, Load more, `?video=`): the section re-renders only for its own
 * changes.
 */

const TITLE = "Consumer Voice";
const SECTION_ID = SECTION_IDS.consumerVoice;
const SETTINGS_PATH = "this product's Settings → Consumer Voice";

// The state blocks' glyphs, at PageScorePanel.tsx's 20px (:366, :418).
const LOCK_ICON = (
  <span className="inline-flex" aria-hidden="true">
    {glyph("lock", 20, 1.6)}
  </span>
);
const MESSAGE_ICON = (
  <span className="inline-flex" aria-hidden="true">
    {glyph("message", 20, 1.6)}
  </span>
);
const ALERT_ICON = <IconAlertGlyph size={20} strokeWidth={1.7} />;
const CLOCK_ICON = <IconClockGlyph size={20} strokeWidth={1.7} />;

interface Props {
  /** The header payload — the same object the page hands the settings modal. */
  product: ISingleProduct;
  /** `useNetworkScope().brandId`, passed in by the page (the modal's fallback brand). */
  scopeBrandId: number | null;
  /** Bumped by the page when the settings modal closes: a silent reload. */
  reloadKey: number;
  /** The settings modal is open: the 8s poll waits (its close bumps `reloadKey`). */
  pollPaused?: boolean;
  /** The page's `?video=` view hides the section without unmounting it. */
  className?: string;
}

const ConsumerVoiceSectionView: FC<Props> = ({ product, scopeBrandId, reloadKey, pollPaused, className }) => {
  const desireid = product?.desireid ?? null;
  const { role } = useUser();
  // The modal's brand rule (MODAL/index.tsx:64-65), except that a brand
  // viewer whose own regular brand is on the product reads that brand: the
  // page lists on load, and the modal's pick can be a brand they can't read
  // (403 + the global toast on every load). See cvBrandIdForViewer.
  // Null = no call (na-network).
  const internal = isInternal(role);
  const brandId = cvBrandIdForViewer(product?.brands, scopeBrandId, internal);
  const cv = useProductConsumerVoice(desireid, brandId, reloadKey, pollPaused);
  const { rows, ready, item } = cv;
  const view = cvView(rows, cv.load, ready);

  // Retry swaps the error block for the skeleton, and its button (focused)
  // goes with it: once the section lands, focus goes to the first button that
  // replaced it (Retry again, after another failure) — only while the reader
  // is still here (focusReturn). The cards have no button: focus stays put.
  const markRetry = useRetryFocus(view !== "loading", () => focusFirstButton(SECTION_ID));
  const retry = () => {
    markRetry();
    cv.retry();
  };
  const settingsHint = cvSettingsHint(rows, cvSettingsMatchesPage(product?.brands, scopeBrandId, internal));

  const card = (children: ReactNode, subtitle?: ReactNode) => (
    <SectionCard id={SECTION_ID} title={TITLE} subtitle={subtitle} className={className}>
      {children}
    </SectionCard>
  );

  if (view === "ready" && ready) {
    return card(
      <CvScoreCards run={ready} meta={cvReportMeta(item.audit)} metaLoading={item.loading} metaFailed={item.error} />,
      analysedLine(ready),
    );
  }

  // The states, in the video page's Page score vocabulary (PageScorePanel.tsx:355-430).
  if (view === "na-network" || view === "na-access") {
    return card(
      <ScoreState
        skin="empty"
        icon={LOCK_ICON}
        title="Not available"
        body={
          view === "na-network"
            ? "No manufacturer brand on this product, so there's nothing to analyse."
            : "Consumer Voice isn't available for this product in your workspace."
        }
      />,
    );
  }

  if (view === "error") {
    // Announced: each failed load mounts it afresh (Retry shows the skeleton
    // first), so a failed Retry is announced again.
    return card(
      <div role="alert">
        <ScoreState
          skin="error"
          icon={ALERT_ICON}
          title="Couldn't load Consumer Voice"
          body="Something went wrong on our end."
          action={
            <TouchTarget>
              <RetryButton onClick={retry} />
            </TouchTarget>
          }
        />
      </div>,
    );
  }

  if (view === "running") {
    return card(
      <ScoreState
        skin="working"
        icon={CLOCK_ICON}
        title="Analysis running"
        body="This usually takes a few minutes. The scores will appear here on their own."
        footer={
          <div className="flex justify-center">
            <PollIndicator>Checking every 8 seconds</PollIndicator>
          </div>
        }
      />,
    );
  }

  // A pending run past the 60-minute window (cvView): the modal still locks
  // Run as "Analysis running…", so no pointer at Settings. The video page's
  // stalled attempt in the same skin and glyph (ScoreNowAction.tsx:242-262).
  if (view === "stalled") {
    return card(
      <ScoreState
        skin="empty"
        icon={CLOCK_ICON}
        title="The analysis is taking longer than expected"
        body="It usually takes a few minutes, but this one may still be finishing. Check back later."
      />,
    );
  }

  if (view === "failed") {
    return card(
      <ScoreState
        skin="error"
        icon={ALERT_ICON}
        title="The last analysis didn't finish"
        body={
          settingsHint ? `Run it again from ${SETTINGS_PATH}.` : "Something went wrong while this product was being analysed."
        }
      />,
    );
  }

  if (view === "never") {
    return card(
      <ScoreState
        skin="empty"
        icon={MESSAGE_ICON}
        title="No analysis yet"
        body={
          settingsHint
            ? `Run one from ${SETTINGS_PATH}.`
            : "What shoppers say about this product will appear here once it's analysed."
        }
      />,
    );
  }

  // loading
  return card(<StateBlockSkeleton />);
};

export const ConsumerVoiceSection = React.memo(ConsumerVoiceSectionView);
